import { FC } from 'react';

import { Formik, FormikHelpers } from 'formik';

import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';

import { DailyRewardAvailableTickets } from './DailyRewardAvailableTickets';
import { DailyRewardFormValues } from './types';
import { dailyRewardValidationSchema } from './validationSchema';

import { CheckBoxAutocomplete } from 'v2/components/autocomplete/checkBoxAutocomplete/CheckBoxAutocomplete';
import { RewardsFormControl } from 'v2/components/form/rewardsFormControl/RewardsFormControl';
import { REWARD_STUB, USER_ROLES } from 'v2/constants';

interface DailyRewardFormProps {
  initialValues: DailyRewardFormValues;
  submitButtonCaption?: string;
  isSubmitting?: boolean;
  onSubmit: (values: DailyRewardFormValues, helpers: FormikHelpers<DailyRewardFormValues>) => void | Promise<void>;
}

export const DailyRewardForm: FC<DailyRewardFormProps> = ({
  initialValues,
  isSubmitting = false,
  submitButtonCaption = 'Save',
  onSubmit,
}) => (
  <Formik
    initialValues={initialValues}
    onSubmit={onSubmit}
    validationSchema={dailyRewardValidationSchema}
    enableReinitialize
    validateOnBlur
    validateOnChange={false}
  >
    {(formik) => {
      const { values, handleChange, setFieldValue, handleBlur, errors, touched, handleSubmit, dirty } = formik;
      const {
        day: dayValue,
        description: descriptionValue,
        rewards: rewardsValue,
        allowedUserRoles: allowedUserRolesValue,
      } = values;
      const { day: dayError, description: descriptionError } = errors;
      const { day: isDayTouched, description: isDescriptionTouched } = touched;

      const isDayError = isDayTouched && !!dayError;
      const isDescriptionError = isDescriptionTouched && !!descriptionError;

      const isSubmitButtonDisabled = !dirty;

      return (
        <form className="form" onSubmit={handleSubmit}>
          <TextField
            helperText={isDayError ? dayError : undefined}
            label="Reward Day"
            id="day"
            name="day"
            type="number"
            value={dayValue}
            onBlur={handleBlur}
            onChange={handleChange}
            error={isDayError}
            fullWidth
            required
          />
          <TextField
            helperText={isDescriptionError ? descriptionError : undefined}
            error={isDescriptionError}
            label="Description"
            id="description"
            name="description"
            value={descriptionValue}
            onBlur={handleBlur}
            onChange={handleChange}
            inputProps={{ maxLength: 150 }}
            minRows={5}
            multiline
            fullWidth
            required
          />
          <CheckBoxAutocomplete
            label="Roles"
            value={allowedUserRolesValue}
            options={USER_ROLES}
            onChange={(v) => setFieldValue('allowedUserRoles', v)}
            required
          />
          <DailyRewardAvailableTickets rewards={rewardsValue} />
          <RewardsFormControl formik={formik} initialValue={REWARD_STUB} required />
          <Button
            type="submit"
            variant="contained"
            loading={isSubmitting}
            disabled={isSubmitting || isSubmitButtonDisabled}
          >
            {submitButtonCaption}
          </Button>
        </form>
      );
    }}
  </Formik>
);
